
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import {mapActions, mapGetters, mapState} from 'vuex';
    import { SweetAlertResult } from 'sweetalert2';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import ElgaImpfstoffe from '@/types/ElgaImpfstoffe';
    import Impfstoff from '@/types/Impfstoffe';
    import ImpfstoffeFolgeimpfstoffe from '@/components/Impfstoffe/ImpfstoffeFolgeimpfstoffe.vue';
    import ImpfstoffeTeilimpfungen from '@/components/Impfstoffe/ImpfstoffeTeilimpfungen.vue';
    import {
        DeleteImpfstoffPayload,
        ImpfstoffeFilter,
        RefreshImpfabstaendePayload,
        UpdateImpfstoffPayload,
    } from '@/store/impfstoffe';
    import { VCurrencyField, VForm } from '@/types/Components';
    import { confirmSwal } from '@/helper/swals';
    import api from '@/services/Api';
    import { AxiosResponse } from 'axios';

    @Component({
        components: {
            ImpfstoffeFolgeimpfstoffe,
            ImpfstoffeTeilimpfungen,
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules']),
            ...mapState('impfstoff', ['elgaimpfstoffe', 'impfdbimpfstoffe']),
            ...mapState('impfstoffe', ['impfstoffe', 'loaders']),
            ...mapGetters('authentication', ['getSystemEinstellung']),
        },
        methods: {
            ...mapActions('impfstoffe', ['updateImpfstoff', 'refreshImpfabstaende', 'deleteImpfstoff']),
        },
    })
    export default class ImpfstoffeDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() impfstoff!: Impfstoff;
        @Prop() filter!: ImpfstoffeFilter;

        public $refs!: {
            form: VForm;
            verkaufspreis: VCurrencyField;
            preis_mitarbeiter: VCurrencyField;
            preis_rektorat: VCurrencyField;
        };

        public impfstoffe!: Impfstoff[];
        public elgaimpfstoffe!: ElgaImpfstoffe[];
        public updateImpfstoff!: (payload: UpdateImpfstoffPayload) => Promise<void>;
        public refreshImpfabstaende!: (payload: RefreshImpfabstaendePayload) => void;
        public deleteImpfstoff!: (payload: DeleteImpfstoffPayload) => void;

        public loadingTeilimpfungen = false;
        public loadingFolgeimpfstoffe = false;
        public defaultVerkaufspreis = 0;
        public folgeimpfstoffeError = false;
        public teilimpfungsnrError = false;
        public selectedElgaImpfstoff = new ElgaImpfstoffe();
        public textErrorMessage = '';
        public tabIndex = 'tab-allgemein';
        public impfstoffName = '';

        public get isEdit(): boolean {
            return this.impfstoff.impfstoff_id !== null;
        }

        public get elgaIndikation(): string | null {
            return this.selectedElgaImpfstoff.target_displayname ?? this.selectedElgaImpfstoff.target01_displayname;
        }

        public get hasImpfschemen(): boolean {
            return !!this.selectedElgaImpfstoff.impfschemen && this.selectedElgaImpfstoff.impfschemen.length > 0;
        }

        public get folgeimpfstoffe(): Impfstoff[] {
            return this.impfstoffe.filter(({ impfstoff_id }) => impfstoff_id !== this.impfstoff.impfstoff_id);
        }

        public get textLabelClass(): string {
            return this.textErrorMessage === ''
                ? 'primary--text'
                : 'red--text';
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                this.tabIndex = 'tab-allgemein';
                this.impfstoffName = this.impfstoff.impfstoff ?? '';
                this.folgeimpfstoffeError = false;
                this.teilimpfungsnrError = false;
                this.defaultVerkaufspreis = this.impfstoff.verkaufspreis ?? 0;
                this.textErrorMessage = '';

                this.$nextTick(() => {
                    this.$refs.verkaufspreis.setValue(this.defaultVerkaufspreis);
                });

                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
            }
        }

        @Watch('impfstoff.impfstoff_id')
        public onChangeImpfstoff(impfstoff_id: number | null): void {
            if (impfstoff_id) {
                this.loadTeilimpfungen();
                this.loadFolgeimpfstoffe();
                this.impfstoffName = this.impfstoff.impfstoff ?? '';
            }
        }

        @Watch('impfstoff.impfstoff_code')
        public onChangeImpfstoffCode(code: string): void {
            this.selectedElgaImpfstoff =
                this.elgaimpfstoffe.find(({ impfstoff_code }) => impfstoff_code === code) ?? new ElgaImpfstoffe();
        }

        public loadTeilimpfungen(): void {
            this.loadingTeilimpfungen = true;

            api.post('verwaltung/teilimpfungen/load', { impfstoff_id: this.impfstoff.impfstoff_id })
                .then((response: AxiosResponse) => (this.impfstoff.teilimpfungen = response.data.body))
                .catch(console.error)
                .finally(() => this.loadingTeilimpfungen = false);
        }

        public loadFolgeimpfstoffe(): void {
            this.loadingFolgeimpfstoffe = true;

            api.post('verwaltung/folgeimpfstoffe', { impfstoff_id: this.impfstoff.impfstoff_id })
                .then((response: AxiosResponse) => (this.impfstoff.folgeimpfstoffe = response.data.body))
                .catch(console.error)
                .finally(() => this.loadingFolgeimpfstoffe = false);
        }

        public onClickSave(): void {
            this.folgeimpfstoffeError = this.impfstoff.folgeimpfstoffe
                .some((folgeimpfstoff) => folgeimpfstoff.folge_impfstoff_id === null) ?? false;

            this.teilimpfungsnrError = this.impfstoff.teilimpfungen
                .some((teilimpfung) => teilimpfung.teilimpfungsnr === null) ?? false;

            if (this.$refs.form.validate() && !this.folgeimpfstoffeError && !this.teilimpfungsnrError) {
                this.updateImpfstoff({ impfstoff: this.impfstoff, filter: this.filter })
                    .then(() => this.impfstoffName = this.impfstoff.impfstoff ?? '');
            } else if (this.folgeimpfstoffeError) {
                this.tabIndex = 'tab-impferinnerungen';
            }
        }

        public onClickRefreshImpfabstaende(): void {
            confirmSwal(
                'Impfabstände',
                'Die Impfabstände aller Impfungen dieses Impfstoffes werden neu berechnet. Sind Sie sicher?',
                'Aktualisieren',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.refreshImpfabstaende({ impfstoff: this.impfstoff });
                }
            });
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Impfstoff endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteImpfstoff({ impfstoff: this.impfstoff, filter: this.filter });
                    }
                });
        }
    }
